import React, { useState } from 'react'
import { X, Smartphone, Shield, Zap, Users, TrendingUp, Award, Star } from 'lucide-react'

interface WalletRecommendationsProps {
  visible: boolean
  onClose: () => void
  onWalletSelect: (walletName: string) => void
}

interface WalletRecommendation {
  name: string
  category: 'hot' | 'hardware'
  useCases: string[]
  pros: string[]
  cons: string[]
  icon: React.ReactNode
  color: string
  recommended: boolean
}

export const WalletRecommendations: React.FC<WalletRecommendationsProps> = ({
  visible,
  onClose,
  onWalletSelect
}) => {
  const [selectedUseCase, setSelectedUseCase] = useState<string>('all')

  const useCases = [
    { id: 'all', label: 'All Use Cases', icon: <Star className="h-4 w-4" /> },
    { id: 'beginner', label: 'First Time User', icon: <Users className="h-4 w-4" /> },
    { id: 'defi', label: 'DeFi & Trading', icon: <TrendingUp className="h-4 w-4" /> },
    { id: 'nft', label: 'NFT Collector', icon: <Award className="h-4 w-4" /> },
    { id: 'mobile', label: 'Mobile User', icon: <Smartphone className="h-4 w-4" /> },
    { id: 'security', label: 'Maximum Security', icon: <Shield className="h-4 w-4" /> },
    { id: 'staking', label: 'Staking Focused', icon: <Zap className="h-4 w-4" /> },
  ]

  const walletRecommendations: WalletRecommendation[] = [
    {
      name: 'Phantom',
      category: 'hot',
      useCases: ['beginner', 'defi', 'nft', 'mobile'],
      pros: ['User-friendly interface', 'Built-in swap & stake', 'Mobile app', 'NFT gallery'],
      cons: ['Hot wallet (less secure)', 'Requires internet'],
      icon: <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center text-white font-bold">P</div>,
      color: 'purple',
      recommended: true
    },
    {
      name: 'Solflare',
      category: 'hot',
      useCases: ['staking', 'security'],
      pros: ['Excellent staking features', 'Hardware wallet support', 'Open source'],
      cons: ['More complex for beginners', 'Less DeFi integrations'],
      icon: <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center text-white font-bold">S</div>,
      color: 'blue',
      recommended: true
    },
    {
      name: 'Backpack',
      category: 'hot',
      useCases: ['nft', 'defi'],
      pros: ['Built for NFTs & xNFTs', 'Multi-chain support', 'Modern interface'],
      cons: ['Newer wallet', 'Limited mobile features'],
      icon: <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center text-white font-bold">B</div>,
      color: 'indigo',
      recommended: false
    },
    {
      name: 'Trust Wallet',
      category: 'hot',
      useCases: ['mobile', 'beginner'],
      pros: ['Mobile-first design', 'Multi-chain support', 'Easy to use'],
      cons: ['Less Solana-specific features', 'Centralized company'],
      icon: <div className="w-8 h-8 bg-cyan-500 rounded-lg flex items-center justify-center text-white font-bold">T</div>,
      color: 'cyan',
      recommended: false
    },
    {
      name: 'Exodus',
      category: 'hot',
      useCases: ['beginner', 'mobile'],
      pros: ['Beautiful interface', 'Desktop & mobile', 'Built-in exchange'],
      cons: ['Closed source', 'Higher fees'],
      icon: <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center text-white font-bold">E</div>,
      color: 'blue',
      recommended: false
    },
    {
      name: 'Ledger',
      category: 'hardware',
      useCases: ['security', 'staking'],
      pros: ['Secure Element chip', 'Offline storage', 'Industry standard'],
      cons: ['Requires hardware purchase', 'Complex setup'],
      icon: <Shield className="w-8 h-8 text-blue-400" />,
      color: 'blue',
      recommended: true
    },
    {
      name: 'Keystone',
      category: 'hardware',
      useCases: ['security'],
      pros: ['Air-gapped security', 'QR code signing', 'Open source'],
      cons: ['Requires hardware purchase', 'QR code workflow'],
      icon: <Shield className="w-8 h-8 text-purple-400" />,
      color: 'purple',
      recommended: true
    },
    {
      name: 'Tangem',
      category: 'hardware',
      useCases: ['security', 'mobile'],
      pros: ['No seed phrase needed', 'Credit card form', 'NFC technology'],
      cons: ['Requires NFC device', 'Newer technology'],
      icon: <Shield className="w-8 h-8 text-green-400" />,
      color: 'green',
      recommended: false
    },
  ]

  const filteredWallets = walletRecommendations.filter(wallet => {
    if (selectedUseCase === 'all') return true
    return wallet.useCases.includes(selectedUseCase)
  })

  if (!visible) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-gray-900 rounded-xl border border-gray-700 w-full max-w-6xl max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-white">Wallet Recommendations</h2>
            <p className="text-gray-400">Find the perfect wallet for your needs</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>

        <div className="p-6">
          {/* Use Case Filter */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-white mb-4">What will you primarily use your wallet for?</h3>
            <div className="flex flex-wrap gap-3">
              {useCases.map((useCase) => (
                <button
                  key={useCase.id}
                  onClick={() => setSelectedUseCase(useCase.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-pill border transition-colors ${
                    selectedUseCase === useCase.id
                      ? 'border-blue-500 bg-blue-500/20 text-blue-400'
                      : 'border-gray-600 bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  {useCase.icon}
                  <span className="text-sm font-medium">{useCase.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Wallet Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredWallets.map((wallet) => (
              <div
                key={wallet.name}
                className={`bg-gray-800 rounded-xl p-6 border transition-all hover:border-${wallet.color}-500/50 hover:bg-gray-750 cursor-pointer group relative`}
                onClick={() => {
                  onWalletSelect(wallet.name)
                  onClose()
                }}
              >
                {/* Recommended Badge */}
                {wallet.recommended && (
                  <div className="absolute -top-2 -right-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                    RECOMMENDED
                  </div>
                )}

                {/* Header */}
                <div className="flex items-center space-x-3 mb-4">
                  {wallet.icon}
                  <div>
                    <h4 className="text-lg font-bold text-white">{wallet.name}</h4>
                    <p className="text-sm text-gray-400 capitalize">
                      {wallet.category} wallet
                    </p>
                  </div>
                </div>

                {/* Pros */}
                <div className="mb-4">
                  <h5 className="text-sm font-semibold text-green-400 mb-2">Pros:</h5>
                  <ul className="space-y-1">
                    {wallet.pros.map((pro, index) => (
                      <li key={index} className="text-sm text-gray-300 flex items-start">
                        <span className="text-green-400 mr-2">✓</span>
                        {pro}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Cons */}
                <div className="mb-4">
                  <h5 className="text-sm font-semibold text-red-400 mb-2">Cons:</h5>
                  <ul className="space-y-1">
                    {wallet.cons.map((con, index) => (
                      <li key={index} className="text-sm text-gray-400 flex items-start">
                        <span className="text-red-400 mr-2">×</span>
                        {con}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Select Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onWalletSelect(wallet.name)
                    onClose()
                  }}
                  className={`w-full py-2 px-4 rounded-pill font-medium transition-colors bg-${wallet.color}-600 hover:bg-${wallet.color}-700 text-white`}
                >
                  Select {wallet.name}
                </button>
              </div>
            ))}
          </div>

          {/* Summary Table */}
          <div className="mt-12 bg-gray-800 rounded-xl p-6 border border-gray-700">
            <h3 className="text-xl font-bold text-white mb-6">Quick Reference Guide</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-3 text-gray-400 font-semibold">Use Case</th>
                    <th className="text-left py-3 text-gray-400 font-semibold">Recommended Wallet(s)</th>
                    <th className="text-left py-3 text-gray-400 font-semibold">Type</th>
                  </tr>
                </thead>
                <tbody className="text-gray-300">
                  <tr className="border-b border-gray-700/50">
                    <td className="py-3 font-medium">First time user</td>
                    <td className="py-3">Phantom, Trust Wallet</td>
                    <td className="py-3">Hot wallet</td>
                  </tr>
                  <tr className="border-b border-gray-700/50">
                    <td className="py-3 font-medium">DeFi & Trading</td>
                    <td className="py-3">Phantom, Backpack</td>
                    <td className="py-3">Hot wallet</td>
                  </tr>
                  <tr className="border-b border-gray-700/50">
                    <td className="py-3 font-medium">NFT Collector</td>
                    <td className="py-3">Phantom, Backpack</td>
                    <td className="py-3">Hot wallet</td>
                  </tr>
                  <tr className="border-b border-gray-700/50">
                    <td className="py-3 font-medium">Staking focused</td>
                    <td className="py-3">Solflare, Ledger</td>
                    <td className="py-3">Hot/Hardware</td>
                  </tr>
                  <tr className="border-b border-gray-700/50">
                    <td className="py-3 font-medium">Mobile user</td>
                    <td className="py-3">Trust Wallet, Phantom</td>
                    <td className="py-3">Hot wallet</td>
                  </tr>
                  <tr>
                    <td className="py-3 font-medium">Maximum security</td>
                    <td className="py-3">Ledger, Keystone</td>
                    <td className="py-3">Hardware wallet</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default WalletRecommendations